import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/apiClient';
import { useProjects } from '../../contexts/ProjectContext';

const BusinessPage = ({ isSettings = false }) => {
  const navigate = useNavigate();
  const { currentProject, loadCurrentProject } = useProjects();

  const [formData, setFormData] = useState({
    website: '',
    businessName: '',
    description: '',
    targetAudiences: [],
  });
  const [audienceInput, setAudienceInput] = useState('');
  const [loading, setLoading]             = useState(false);
  const [errorMsg, setErrorMsg]           = useState('');

  // When the loaded project changes, sync form fields (empty deps missed context after first paint)
  useEffect(() => {
    if (!currentProject?.id) return;
    setFormData({
      website: currentProject.website || '',
      businessName: currentProject.business_name || '',
      description: currentProject.description || '',
      targetAudiences: currentProject.target_audiences || [],
    });
  }, [currentProject?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(fd => ({ ...fd, [name]: value }));
  };

  const handleAddAudience = (e) => {
    if ((e.key === 'Enter' || e.type === 'click') && audienceInput.trim()) {
      e.preventDefault();
      setFormData(fd => ({
        ...fd,
        targetAudiences: [...fd.targetAudiences, audienceInput.trim()],
      }));
      setAudienceInput('');
    }
  };

  const handleRemoveAudience = (idx) => {
    setFormData(fd => ({
      ...fd,
      targetAudiences: fd.targetAudiences.filter((_, i) => i !== idx),
    }));
  };

  const handleAutoComplete = async () => {
    setErrorMsg('');
    setLoading(true);
    
    try {
      // Validate URL format
      if (!formData.website.match(/^https?:\/\//i)) {
        throw new Error('Please enter a valid URL starting with http:// or https://');
      }
      
      // Call the website analysis API
      const response = await api.websiteAnalysis.analyze(formData.website);
      
      // Update the form with the API response
      setFormData(fd => ({
        ...fd,
        businessName: response.data.businessName,
        description: response.data.description,
        targetAudiences: response.data.targetAudiences,
      }));
    } catch (err) {
      console.error('Error analyzing website:', err);
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to analyze website');
    } finally {
      setLoading(false);
    }
  };

  // 2️⃣ On submit, either create or update the business step
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);

    const payload = {
      website:          formData.website,
      business_name:    formData.businessName,
      description:      formData.description,
      target_audiences: formData.targetAudiences,
    };

    try {
      let projectId;
      if (currentProject?.id) {
        // PATCH /api/projects/:id/business
        await api.projects.updateBusiness(currentProject.id, payload);
        projectId = currentProject.id;
      } else {
        // POST /api/projects
        const res = await api.projects.create(payload);
        projectId = res.data.id;
        localStorage.setItem('currentProjectId', projectId);
      }
      await loadCurrentProject(projectId, { force: true });
      
      // If in settings mode, don't navigate
      if (!isSettings) {
        navigate('/onboarding/competitors');
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8">
      {!isSettings && (
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
          Tell us about your business
        </h1>
      )}

      {errorMsg && (
        <div className="bg-red-100 text-red-700 p-2 rounded mb-4">
          {errorMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        {/* Website */}
        <div>
          <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
            Website to Business
          </label>
          <input
            type="url"
            id="website"
            name="website"
            placeholder="https://example.com"
            value={formData.website}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Autocomplete */}
        <div>
          <button
            type="button"
            onClick={handleAutoComplete}
            disabled={!formData.website || loading}
            className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-teal-400 hover:bg-teal-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-400 disabled:opacity-50"
          >
            {loading ? (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            ) : (
              <>Autocomplete With AI</>
            )}
          </button>
        </div>

        {/* Business Details */}
        <div className="bg-gray-50 p-6 rounded-lg space-y-6">
          {/* Name */}
          <div>
            <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
              Business Name
            </label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              placeholder="Your Business Name"
              value={formData.businessName}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              rows={4}
              placeholder="Describe your business"
              value={formData.description}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Target Audiences */}
          <div>
            <label htmlFor="targetAudience" className="block text-sm font-medium text-gray-700 mb-1">
              Add Target Audiences
            </label>
            <div className="flex">
              <input
                type="text"
                id="targetAudience"
                value={audienceInput}
                onChange={e => setAudienceInput(e.target.value)}
                onKeyDown={handleAddAudience}
                placeholder="Type and press enter"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={handleAddAudience}
                className="px-3 py-2 border border-gray-300 border-l-0 rounded-r-md bg-gray-100 hover:bg-gray-200"
              >
                +
              </button>
            </div>
            {formData.targetAudiences.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.targetAudiences.map((aud, idx) => (
                  <div key={idx} className="bg-gray-200 px-3 py-1 rounded-full text-sm flex items-center">
                    {aud}
                    <button
                      type="button"
                      onClick={() => handleRemoveAudience(idx)}
                      className="ml-1 text-gray-500 hover:text-gray-700"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Continue */}
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
          >
            {loading ? 'Saving…' : isSettings ? 'Save Changes' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessPage;
